import api from './api';

export interface UserLevel {
  id: string;
  name: string;
  description?: string;
  level: number;
  color?: string;
  createdAt: string;
  userCount: number;
}

export interface CreateUserLevelRequest {
  name: string;
  description?: string;
  level: number;
  color?: string;
}

export interface UpdateUserLevelRequest {
  name: string;
  description?: string;
  level: number;
  color?: string;
}

export interface Skill {
  id: string;
  name: string;
  description?: string;
  category: string;
  icon?: string;
  createdAt: string;
  userCount: number;
}

export interface CreateSkillRequest {
  name: string;
  description?: string;
  category: string;
  icon?: string;
}

export interface UpdateSkillRequest {
  name: string;
  description?: string;
  category: string;
  icon?: string;
}

export interface UserSkill {
  id: string;
  skillId: string;
  skill?: Skill;
  proficiencyLevel: string;
  yearsOfExperience?: number;
  acquiredAt: string;
  lastUsedAt?: string;
  notes?: string;
}

export interface User {
  id: string;
  firstName: string;
  lastName: string;
  fullName: string;
  email: string;
  profilePictureUrl?: string;
  preferredLanguage: string;
  timeZone: string;
  userLevelId?: string;
  userLevel?: UserLevel;
  skills: UserSkill[];
  createdAt: string;
  lastLoginAt?: string;
  isActive: boolean;
}

export interface UpdateUserRequest {
  firstName: string;
  lastName: string;
  preferredLanguage?: string;
  timeZone?: string;
  userLevelId?: string;
  profilePictureUrl?: string;
}

export interface CreateUserRequest {
  email: string;
  firstName: string;
  lastName: string;
  password?: string;
  userLevelId?: string;
  preferredLanguage?: string;
  timeZone?: string;
  roleName?: string;
}

export interface AssignSkillRequest {
  skillId: string;
  proficiencyLevel: string;
  yearsOfExperience?: number;
  acquiredAt?: string;
  lastUsedAt?: string;
  notes?: string;
}

export interface UpdateUserSkillRequest {
  proficiencyLevel: string;
  yearsOfExperience?: number;
  lastUsedAt?: string;
  notes?: string;
}

// User Levels API
export const getUserLevels = async (): Promise<UserLevel[]> => {
  const response = await api.get('/userlevels');
  return response.data;
};

export const getUserLevel = async (id: string): Promise<UserLevel> => {
  const response = await api.get(`/userlevels/${id}`);
  return response.data;
};

export const createUserLevel = async (data: CreateUserLevelRequest): Promise<UserLevel> => {
  const response = await api.post('/userlevels', data);
  return response.data;
};

export const updateUserLevel = async (id: string, data: UpdateUserLevelRequest): Promise<void> => {
  await api.put(`/userlevels/${id}`, data);
};

export const deleteUserLevel = async (id: string): Promise<void> => {
  await api.delete(`/userlevels/${id}`);
};

// Skills API
export const getSkills = async (category?: string): Promise<Skill[]> => {
  const params = category ? { category } : {};
  const response = await api.get('/skills', { params });
  return response.data;
};

export const getSkillCategories = async (): Promise<string[]> => {
  const response = await api.get('/skills/categories');
  return response.data;
};

export const getSkill = async (id: string): Promise<Skill> => {
  const response = await api.get(`/skills/${id}`);
  return response.data;
};

export const createSkill = async (data: CreateSkillRequest): Promise<Skill> => {
  const response = await api.post('/skills', data);
  return response.data;
};

export const updateSkill = async (id: string, data: UpdateSkillRequest): Promise<void> => {
  await api.put(`/skills/${id}`, data);
};

export const deleteSkill = async (id: string): Promise<void> => {
  await api.delete(`/skills/${id}`);
};

// Users API
export const getUsers = async (levelId?: string, skillId?: string): Promise<User[]> => {
  const params: any = {};
  if (levelId) params.levelId = levelId;
  if (skillId) params.skillId = skillId;
  const response = await api.get('/users', { params });
  return response.data;
};

export const getUser = async (id: string): Promise<User> => {
  const response = await api.get(`/users/${id}`);
  return response.data;
};
export const createUser = async (data: CreateUserRequest): Promise<User> => {
  const response = await api.post<User>('/users', data);
  return response.data;
};
export const updateUser = async (id: string, data: UpdateUserRequest): Promise<void> => {
  await api.put(`/users/${id}`, data);
};

export const getUserSkills = async (userId: string): Promise<UserSkill[]> => {
  const response = await api.get(`/users/${userId}/skills`);
  return response.data;
};

export const assignSkill = async (userId: string, data: AssignSkillRequest): Promise<UserSkill> => {
  const response = await api.post(`/users/${userId}/skills`, data);
  return response.data;
};

export const updateUserSkill = async (
  userId: string,
  skillId: string,
  data: UpdateUserSkillRequest
): Promise<void> => {
  await api.put(`/users/${userId}/skills/${skillId}`, data);
};

export const removeUserSkill = async (userId: string, skillId: string): Promise<void> => {
  await api.delete(`/users/${userId}/skills/${skillId}`);
};
